/**
 * SendCampaignModal
 * Quick campaign send modal for the contacts management page.
 * Two modes: Use existing DRAFT campaign OR create quick ad-hoc campaign.
 */

import React, { useState, useEffect } from 'react';
import {
  X, Send, Mail, FileText, ChevronDown,
  AlertCircle, CheckCircle, Loader, Users
} from 'lucide-react';

function SendCampaignModal({ isOpen, onClose, selectedContacts, apiWithAccessControl, onSuccess }) {
  const [mode, setMode] = useState('existing'); // 'existing' | 'quick'
  const [draftCampaigns, setDraftCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [quickSubject, setQuickSubject] = useState('');
  const [quickContent, setQuickContent] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadDraftCampaigns();
      setError(null);
      setSending(false);
      setSelectedCampaignId('');
      setQuickSubject('');
      setQuickContent('');
    }
  }, [isOpen]);

  const loadDraftCampaigns = async () => {
    setLoadingDrafts(true);
    try {
      const res = await apiWithAccessControl('/admin/campaigns/drafts');
      const data = await res.json();
      if (data.success) {
        setDraftCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Error loading draft campaigns:', err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const getTypeBreakdown = () => {
    const breakdown = {};
    (selectedContacts || []).forEach(c => {
      const type = c.contactType || 'UNKNOWN';
      breakdown[type] = (breakdown[type] || 0) + 1;
    });
    return breakdown;
  };

  const canSend = () => {
    if (sending) return false;
    if (!selectedContacts || selectedContacts.length === 0) return false;
    if (mode === 'existing') return !!selectedCampaignId;
    if (mode === 'quick') return quickSubject.trim() && quickContent.trim();
    return false;
  };

  const handleSend = async () => {
    if (!canSend()) return;
    setSending(true);
    setError(null);

    try {
      const body = {
        contactIds: selectedContacts.map(c => ({
          id: c.id,
          contactType: c.contactType
        }))
      };

      if (mode === 'existing') {
        body.campaignId = selectedCampaignId;
      } else {
        body.quickCampaign = {
          subject: quickSubject.trim(),
          content: quickContent.trim()
        };
      }

      const res = await apiWithAccessControl('/admin/contacts/send-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (data.success) {
        onSuccess(`Campaign sent to ${data.recipientCount} recipients`);
      } else {
        setError(data.message || 'Failed to send campaign');
      }
    } catch (err) {
      setError('Failed to send campaign. Please try again.');
      console.error('Send campaign error:', err);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const breakdown = getTypeBreakdown();
  const contactCount = selectedContacts?.length || 0;

  return (
    <div className="mkt-con-modal-overlay" onClick={onClose}>
      <div className="mkt-con-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        {/* Header */}
        <div className="mkt-con-modal-header">
          <h3>Send Campaign</h3>
          <button className="mkt-con-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="mkt-con-modal-body">
          {/* Recipient Summary */}
          <div className="mkt-con-send-recipients">
            <div className="mkt-con-send-recipient-header">
              <Users size={16} />
              <span><strong>{contactCount}</strong> recipient{contactCount !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="mkt-con-send-breakdown">
              {Object.entries(breakdown).map(([type, count]) => (
                <span key={type} className={`mkt-con-type-badge mkt-con-type-${type.toLowerCase()}`}>
                  {type}: {count}
                </span>
              ))}
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="mkt-con-send-mode-toggle">
            <button
              className={`mkt-con-send-mode-btn ${mode === 'existing' ? 'active' : ''}`}
              onClick={() => setMode('existing')}
            >
              <FileText size={16} />
              Use Existing Campaign
            </button>
            <button
              className={`mkt-con-send-mode-btn ${mode === 'quick' ? 'active' : ''}`}
              onClick={() => setMode('quick')}
            >
              <Mail size={16} />
              Quick Campaign
            </button>
          </div>

          {/* Existing Campaign Picker */}
          {mode === 'existing' && (
            <div className="mkt-con-send-existing">
              <label>Select a draft campaign</label>
              {loadingDrafts ? (
                <p className="mkt-con-text-muted">Loading campaigns...</p>
              ) : draftCampaigns.length === 0 ? (
                <div className="mkt-con-send-no-drafts">
                  <AlertCircle size={16} />
                  <span>No draft campaigns available. Create one in the Campaigns tab or use Quick Campaign mode.</span>
                </div>
              ) : (
                <select
                  value={selectedCampaignId}
                  onChange={e => setSelectedCampaignId(e.target.value)}
                  className="mkt-con-send-select"
                >
                  <option value="">-- Select a campaign --</option>
                  {draftCampaigns.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} — "{c.subject}"
                    </option>
                  ))}
                </select>
              )}
              {selectedCampaignId && (
                <div className="mkt-con-send-campaign-preview">
                  {(() => {
                    const selected = draftCampaigns.find(c => c.id === selectedCampaignId);
                    if (!selected) return null;
                    return (
                      <>
                        <strong>{selected.name}</strong>
                        <span>Subject: {selected.subject}</span>
                        <span>Type: {selected.type}</span>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Quick Campaign Form */}
          {mode === 'quick' && (
            <div className="mkt-con-send-quick">
              <div className="mkt-con-modal-field">
                <label>Subject Line *</label>
                <input
                  type="text"
                  value={quickSubject}
                  onChange={e => setQuickSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className="mkt-con-send-input"
                />
              </div>
              <div className="mkt-con-modal-field">
                <label>Email Content *</label>
                <textarea
                  value={quickContent}
                  onChange={e => setQuickContent(e.target.value)}
                  placeholder="Enter email content (HTML supported)..."
                  className="mkt-con-send-textarea"
                  rows={8}
                />
              </div>
              <div className="mkt-con-send-hint">
                <strong>Available variables:</strong><br/>
                {'{{firstName}}'}, {'{{lastName}}'}, {'{{email}}'}, {'{{merchantName}}'}, {'{{storeName}}'}, {'{{storeUrl}}'}, {'{{websiteUrl}}'}, {'{{supportEmail}}'}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mkt-con-send-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mkt-con-modal-footer">
          <button className="mkt-con-btn mkt-con-btn-outline" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="mkt-con-btn mkt-con-btn-primary"
            onClick={handleSend}
            disabled={!canSend()}
          >
            {sending ? (
              <>
                <Loader size={16} className="mkt-con-spinner-inline" />
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send to {contactCount} Recipient{contactCount !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SendCampaignModal;
